import {
  CaseDetailDTO,
  CaseDetailAssetDTO,
  CaseDetailActivitySegmentDTO,
  CaseDetailMetricDTO,
  CaseDetailMetricSessionDTO,
  CaseSummaryDTO,
} from './CaseDTOs';
import {
  AlignmentMetric,
  CannulationTime,
  CaseReportTotals,
  CaseAsset,
  CaseReport,
  CaseReportSegment,
  CaseReportSummary,
  MetricCategories,
  MetricSession,
  MetricSessionTypes
} from './CaseTypes';

export default class CaseReportAdapter {

  /**
   * Calculates total duration times for activity segments.
   * Supports both combined (cannulation/screw placement) and split activity types.
   *
   * @param segments
   * @returns
   */
  calculateActivityDurations(segments: CaseReportSegment[]): CaseReportTotals {
    // Standard activity durations
    const alignment_decompression_time = this.calculateSegmentDurationMinutes(segments, 'decompression/alignment');
    const closure_time = this.calculateSegmentDurationMinutes(segments, 'closure');
    const exposure_time = this.calculateSegmentDurationMinutes(segments, 'exposure');
    const post_cannulation_activity = this.calculateSegmentDurationMinutes(segments, 'post cannulation activity');
    const total_cannulation_screw_placement_time = this.calculateSegmentDurationMinutes(segments, 'cannulation/screw placement');
    const total_iom_time = this.calculateSegmentDurationMinutes(segments, 'intraop measurement');

    // Split activity durations (finer-grained breakdown of cannulation/screw placement)
    const anatomy_localization_time = this.calculateSegmentDurationMinutes(segments, 'anatomy localization');
    const cannulation_time = this.calculateSegmentDurationMinutes(segments, 'cannulation');
    const screw_placement_time = this.calculateSegmentDurationMinutes(segments, 'screw placement');

    // Determine which cannulation time to use for captured operative time
    // If split fields have data, use them; otherwise use combined field
    const has_split_cannulation_data = cannulation_time > 0 || screw_placement_time > 0;
    const cannulationTotal = has_split_cannulation_data
      ? (anatomy_localization_time + cannulation_time + screw_placement_time)
      : total_cannulation_screw_placement_time;

    const captured_operative_time = closure_time
      + alignment_decompression_time
      + exposure_time
      + cannulationTotal;

    const total_navigated_time = this.calculateTotalNavigatedTime(segments);

    return {
      alignment_decompression_time,
      closure_time,
      exposure_time,
      post_cannulation_activity,
      total_cannulation_screw_placement_time: cannulationTotal,
      total_iom_time,
      captured_operative_time,
      total_navigated_time,
      anatomy_localization_time,
      cannulation_time,
      screw_placement_time,
      has_split_cannulation_data,
    };
  }

  calculateTotalNavigatedTime(segments: CaseReportSegment[]): number {
    return segments
      .reduce((acc: number, segment: CaseReportSegment) => {
        return acc + (segment.normalizedEndMin - segment.normalizedStartMin);
      }, 0);
  }

  calculateSegmentDurationMinutes(segments: CaseReportSegment[], segment_type: string): number {
    return segments
      .filter((segment: CaseReportSegment) => segment.segmentType.toLowerCase() === segment_type)
      .reduce((acc: number, segment: CaseReportSegment) => {
        return acc + (segment.normalizedEndMin - segment.normalizedStartMin);
      }, 0);
  }

  getVertebraSortOrder(vertebra: string): number {
    const match = vertebra.match(/([TLS])(\d+)/);
    if (!match) return 999; // Put unknown formats at the end

    const type = match[1];
    const number = parseInt(match[2]);

    // Order by T, then L, then S
    if (type === 'T') return number;
    if (type === 'L') return 100 + number;
    if (type === 'S') return 200 + number;
    return 999;
  };

  mapSegmentToViewModel(segment: CaseDetailActivitySegmentDTO): CaseReportSegment {
    const video_start_s = segment.media_start_ms ? this.msToSeconds(segment.media_start_ms) : null;
    const video_end_s = segment.media_end_ms ? this.msToSeconds(segment.media_end_ms) : null;
    const video_is_valid = segment.media_start_ms !== null && segment.media_end_ms !== null && segment.signed_url;
    return {
      segmentType: segment.activity_name,
      normalizedStartMin: this.msToMinutes(segment.normalized_start_ms),
      normalizedEndMin: this.msToMinutes(segment.normalized_end_ms),
      videoStartSec: video_start_s,
      videoEndSec: video_end_s,
      videoIsValid: video_is_valid,
      segmentValue: segment.locus,
      signedUrl: segment.signed_url
    } as CaseReportSegment;
  }

  mapToSummaryViewModel(caseSummary: CaseSummaryDTO): CaseReportSummary {
    return {
      id: caseSummary.case_id,
      date: new Date(caseSummary.case_date),
      levels: caseSummary.case_levels,
      name: caseSummary.case_name,
      isRevision: caseSummary.is_revision || false,
      isNavigated: caseSummary.is_navigated || false,
      isStaged: caseSummary.is_staged || false,
      stage: caseSummary.stage,

      actors: caseSummary.actors ? caseSummary.actors.map((actor) => ({ actorName: actor.actor_name })) : [],
      procedures: caseSummary.procedures ? caseSummary.procedures.map((procedure) => ({
        procedureType: procedure.procedure_type,
        locusStart: procedure.locus_start,
        locusEnd: procedure.locus_end
      })) : []
    } as CaseReportSummary;
  }

  /**
   * Converts the API result to the ViewModel used by the UI.
   * @param caseDetail
   * @returns
   */
  mapToViewModel(caseDetail: CaseDetailDTO): CaseReport {
    const segments: CaseReportSegment[] = caseDetail.activity_segments ?
      caseDetail.activity_segments.map((segment: CaseDetailActivitySegmentDTO) => (
        this.mapSegmentToViewModel(segment))
      ).sort((a: CaseReportSegment, b: CaseReportSegment) => {
        return a.normalizedStartMin - b.normalizedEndMin;
      }) : [];

    const activityDurations = this.calculateActivityDurations(segments);

    const cannulationTimes = this.mapCannulationTimes(caseDetail);
    const pathLengths = caseDetail.cannulation_paths ?
      caseDetail.cannulation_paths
        .filter((metric: CaseDetailMetricDTO) =>
          metric.unit === 'cm' && metric.locus && metric.instrument
        )
        .map((metric: CaseDetailMetricDTO) => ({
          path_length_meters: metric.value / 100,
          tool_name: metric.instrument as string,
          vertebra: metric.locus as string
        })) : [];

    const alignmentMetrics = this.mapAlignmentMetrics(caseDetail);
    const videos: CaseAsset[] = caseDetail.assets ?
      caseDetail.assets
        .filter(
          asset => asset.category === 'efficiency' && asset.media_location.endsWith('.mp4')
        )
        .map(asset => ({
          category: asset.category,
          signedUrl: asset.signed_url || null
        })) : [];

    return {
      id: caseDetail.case_id,
      date: new Date(caseDetail.case_date),
      hasActivities: caseDetail.activity_segments !== undefined && caseDetail.activity_segments.length > 0,
      hasRecording: videos.length > 0,
      levels: caseDetail.case_levels,
      name: caseDetail.case_name || caseDetail.case_id,
      isRevision: caseDetail.is_revision || false,
      isNavigated: caseDetail.is_navigated || false,
      isStaged: caseDetail.is_staged || false,
      stage: caseDetail.stage,

      actors: caseDetail.actors ? caseDetail.actors.map((actor) => ({ actorName: actor.actor_name })) : [],
      procedures: caseDetail.procedures ? caseDetail.procedures.map((procedure) => ({
        procedureType: procedure.procedure_type,
        locusStart: procedure.locus_start,
        locusEnd: procedure.locus_end
      })) : [],

      alignment: {
        metric_sessions: alignmentMetrics
      },
      pathLengths: pathLengths,
      segments: segments,
      totals: activityDurations,
      cannulationTimes: cannulationTimes,
      videos: videos
    };
  }

  /**
   * Converts a DB session name to a descriptive version for display.
   * @param sessionName The full session name.
   * @returns The descriptive version of the session name.
   */
  getSessionDescription(sessionName: string): string {
    const sessionNameLower = sessionName?.toLowerCase();

    if (sessionNameLower === MetricSessionTypes.POST_OP_ALIGN.toLowerCase()) {
      return 'Post Op Alignment';
    }

    if (sessionNameLower === MetricSessionTypes.PRE_ALIGN.toLowerCase()) {
      return 'Pre Op Alignment';
    }

    if (sessionNameLower.includes(MetricSessionTypes.INTRA_OP_ALIGN_PREFIX.toLowerCase())) {
      return sessionNameLower.replace(MetricSessionTypes.INTRA_OP_ALIGN_PREFIX.toLowerCase(), 'Intraop Update');
    }

    return sessionName;
  }

  /**
   * Converts a DB session name to a shorthand version for display in small areas.
   * @param sessionName The full session name.
   * @returns The shorthand version of the session name.
   */
  getSessionShorthand(sessionName: string): string {
    const sessionNameLower = sessionName?.toLowerCase();
    return sessionNameLower === MetricSessionTypes.POST_OP_ALIGN.toLowerCase() ? 'Post Op' :
      sessionNameLower === MetricSessionTypes.PRE_ALIGN.toLowerCase() ? 'Pre Op' :
        sessionNameLower.includes(MetricSessionTypes.INTRA_OP_ALIGN_PREFIX.toLowerCase()) ?
          sessionNameLower.replace(MetricSessionTypes.INTRA_OP_ALIGN_PREFIX.toLowerCase(), 'Intra Op').trim() :
          sessionName;
  }

  /**
   * Maps alignment metrics to format expected by the view.
   *
   * @param caseDetail
   * @param comparisons
   * @returns
   */
  mapAlignmentMetrics(caseDetail: CaseDetailDTO): MetricSession[] {
    let hasIntraOpSessions:boolean = false;
    const distinctIntraOpMetrics = new Set<string>();

    if (!caseDetail.metric_sessions) {
      return [];
    }

    if (caseDetail.metric_sessions.some((aSession: CaseDetailMetricSessionDTO) =>
      aSession.session.toLowerCase().includes(MetricSessionTypes.INTRA_OP_ALIGN_PREFIX.toLowerCase())
    )) {
      hasIntraOpSessions = true;

      caseDetail.metric_sessions.forEach((aSession: CaseDetailMetricSessionDTO) => {
        if (aSession.session.toLowerCase().includes(MetricSessionTypes.INTRA_OP_ALIGN_PREFIX.toLowerCase())) {
          aSession.metrics.forEach((metric: CaseDetailMetricDTO) => {
            distinctIntraOpMetrics.add(`${metric.label}||${metric.locus}`);
          });
        }
      });
    }

    const metricSessions = [];
    for (const metricSession of caseDetail.metric_sessions) {
      // Search for assets with the same session name
      const alignmentAsset = caseDetail.assets?.find((asset:CaseDetailAssetDTO) =>
        asset.session === metricSession.session &&
        asset.category === MetricCategories.ALIGNMENT
      );

      const validationAsset = caseDetail.assets?.find((asset:CaseDetailAssetDTO) =>
        asset.session === metricSession.session &&
        asset.category === MetricCategories.ALIGNMENT_VALIDATION
      );

      const newMetricSession : MetricSession = {
        sessionName: metricSession.session,
        sessionDescription: this.getSessionDescription(metricSession.session),
        sessionNameShorthand: this.getSessionShorthand(metricSession.session),
        meshAssetSignedUrl: alignmentAsset ? alignmentAsset.signed_url || null : null,
        validationAssetSignedUrl: validationAsset ? validationAsset.signed_url || null : null,
        metrics: metricSession.metrics
          .filter((metric: CaseDetailMetricDTO) => metric.category === MetricCategories.ALIGNMENT)
          .map((metric: CaseDetailMetricDTO) => ({
            id: metric.case_metric_id,
            category: metric.category,
            // Only display metrics if either a) there are no intraop sessions, or
            // b) the metric is in the distinct list of intraop metrics
            display: !hasIntraOpSessions ||
              (hasIntraOpSessions && distinctIntraOpMetrics.has(`${metric.label}||${metric.locus}`)),
            label: metric.label,
            unit: metric.unit === 'degrees' ? '°' : metric.unit,
            vertebra: metric.locus,
            value: Math.round(metric.value * 100) / 100
          })) as AlignmentMetric[]
      };
      metricSessions.push(newMetricSession);
    }
    return metricSessions;
  }

  mapCannulationTimes(caseDetail: CaseDetailDTO): CannulationTime[] {
    const cannulationTimes = caseDetail.cannulation_times ?
      caseDetail.cannulation_times
        .filter((metric: CaseDetailMetricDTO) => metric.unit === 'ms' && metric.locus)
        .map((metric: CaseDetailMetricDTO) => ({
          time_minutes: this.msToMinutes(metric.value),
          vertebra: metric.locus as string
        })) : [];

    // Sort the data by vertebra for consistent display
    const sortedCannulationTimes = cannulationTimes.sort((a: CannulationTime, b: CannulationTime) => {
      // Extract the vertebra level for sorting
      return this.getVertebraSortOrder(a.vertebra) - this.getVertebraSortOrder(b.vertebra);
    });

    return sortedCannulationTimes;
  }

  msToMinutes(ms: number): number {
    return Math.round((ms / 60000) * 100) / 100;
  }

  msToSeconds(ms: number): number {
    return Math.round((ms / 1000) * 100) / 100;
  }
}
